import { useEffect, useState } from "react";
import {
    StarIcon,
    CheckBadgeIcon,
    ChatBubbleLeftRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/solid";

const testimonials = [
    {
        name: "Sarah M.",
        location: "United Kingdom",
        initials: "SM",
        rating: 5,
        text: "The hotel was exactly as described and the booking process was incredibly easy. We found a great deal for our Dubai trip and had a wonderful stay.",
    },
    {
        name: "Ahmed K.",
        location: "Saudi Arabia",
        initials: "AK",
        rating: 5,
        text: "Excellent experience from start to finish. The hotel options were easy to compare and the price was better than many other websites.",
    },
    {
        name: "Maria R.",
        location: "Germany",
        initials: "MR",
        rating: 5,
        text: "We were visiting Dubai for the first time and this made choosing our hotel so much easier. Great recommendations and very smooth booking.",
    },
    {
        name: "James W.",
        location: "United States",
        initials: "JW",
        rating: 5,
        text: "We booked our Dubai hotel through the site and everything went smoothly. The hotel was excellent and the rate was very competitive.",
    },
    {
        name: "Fatima A.",
        location: "Kuwait",
        initials: "FA",
        rating: 5,
        text: "Very easy to use and the hotel choices were great. We found exactly what we were looking for and had a fantastic stay in Dubai.",
    },
    {
        name: "Daniel P.",
        location: "Australia",
        initials: "DP",
        rating: 5,
        text: "Great hotel selection and a very simple booking experience. I would definitely use this website again for my next Dubai trip.",
    },
];

export default function Testimonials() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerView, setItemsPerView] = useState(3);
    const [isPaused, setIsPaused] = useState(false);

    /*
    |--------------------------------------------------------------------------
    | Responsive Items
    |--------------------------------------------------------------------------
    */
    useEffect(() => {
        const updateItemsPerView = () => {
            if (window.innerWidth < 768) {
                setItemsPerView(1);
            } else if (window.innerWidth < 1024) {
                setItemsPerView(2);
            } else {
                setItemsPerView(3);
            }
        };

        updateItemsPerView();

        window.addEventListener("resize", updateItemsPerView);

        return () => {
            window.removeEventListener("resize", updateItemsPerView);
        };
    }, []);

    /*
    |--------------------------------------------------------------------------
    | Maximum Slider Position
    |--------------------------------------------------------------------------
    */
    const maxIndex = Math.max(
        0,
        testimonials.length - itemsPerView
    );

    /*
    |--------------------------------------------------------------------------
    | Keep Index Valid When Screen Size Changes
    |--------------------------------------------------------------------------
    */
    useEffect(() => {
        if (currentIndex > maxIndex) {
            setCurrentIndex(maxIndex);
        }
    }, [maxIndex, currentIndex]);

    /*
    |--------------------------------------------------------------------------
    | Auto Slider
    |--------------------------------------------------------------------------
    */
    useEffect(() => {
        if (isPaused || maxIndex === 0) {
            return;
        }

        const interval = setInterval(() => {
            setCurrentIndex((prev) => {
                if (prev >= maxIndex) {
                    return 0;
                }

                return prev + 1;
            });
        }, 5000);

        return () => clearInterval(interval);
    }, [isPaused, maxIndex]);

    /*
    |--------------------------------------------------------------------------
    | Previous
    |--------------------------------------------------------------------------
    */
    const handlePrevious = () => {
        setCurrentIndex((prev) => {
            if (prev <= 0) {
                return maxIndex;
            }

            return prev - 1;
        });
    };

    /*
    |--------------------------------------------------------------------------
    | Next
    |--------------------------------------------------------------------------
    */
    const handleNext = () => {
        setCurrentIndex((prev) => {
            if (prev >= maxIndex) {
                return 0;
            }

            return prev + 1;
        });
    };

    /*
    |--------------------------------------------------------------------------
    | Dot Count
    |--------------------------------------------------------------------------
    */
    const dotCount = maxIndex + 1;

    return (
        <section className="w-full bg-[#F7FAFC] py-12 sm:py-14 lg:py-16">

            <div className="mx-auto  max-w-[1490px]
              px-4
            sm:px-6
            lg:px-8">

                {/* =====================================================
                    HEADER
                ====================================================== */}
                <div className="mx-auto max-w-2xl text-center">

                    <div className="mb-3 flex items-center justify-center gap-2">

                        <span className="h-[2px] w-7 bg-[#F7B928]" />

                        <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#0B6E8E]">
                            Guest Reviews
                        </span>

                        <span className="h-[2px] w-7 bg-[#F7B928]" />

                    </div>

                    <h2 className="text-2xl font-extrabold tracking-tight text-[#062F4F] sm:text-3xl lg:text-4xl">
                        What Our Guests Say
                    </h2>

                    <p className="mt-3 text-sm leading-6 text-slate-600 sm:text-base">
                        Real experiences from travelers who chose us for their
                        Dubai hotel stay.
                    </p>

                </div>


                {/* =====================================================
                    RATING SUMMARY
                ====================================================== */}
                <div className="mx-auto mt-7 flex w-fit items-center gap-3 rounded-full border border-[#DFE8ED] bg-white px-4 py-2.5 shadow-sm">

                    <div className="flex items-center gap-1">

                        {[1, 2, 3, 4, 5].map((star) => (
                            <StarIcon
                                key={star}
                                className="h-4 w-4 text-[#F7B928]"
                            />
                        ))}

                    </div>

                    <span className="h-4 w-px bg-slate-200" />

                    <span className="text-xs font-bold text-[#062F4F]">
                        4.9 / 5
                    </span>

                    <span className="text-xs text-slate-500">
                        Guest satisfaction
                    </span>

                </div>


                {/* =====================================================
                    SLIDER
                ====================================================== */}
                <div
                    className="relative mt-9"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >

                    {/* Previous Button */}
                    <button
                        type="button"
                        onClick={handlePrevious}
                        aria-label="Previous testimonials"
                        className="
                            absolute
                            left-0
                            top-1/2
                            z-20
                            hidden
                            h-11
                            w-11
                            -translate-x-1/2
                            -translate-y-1/2
                            items-center
                            justify-center
                            rounded-full
                            border
                            border-[#DFE8ED]
                            bg-white
                            text-[#062F4F]
                            shadow-[0_5px_20px_rgba(6,47,79,0.10)]
                            transition-all
                            duration-300
                            hover:bg-[#062F4F]
                            hover:text-white
                            lg:flex
                        "
                    >
                        <ChevronLeftIcon className="h-5 w-5" />
                    </button>


                    {/* Slider Viewport */}
                    <div className="overflow-hidden">

                        <div
                            className="
                                flex
                                transition-transform
                                duration-500
                                ease-out
                            "
                            style={{
                                transform: `translateX(-${currentIndex * (100 / itemsPerView)
                                    }%)`,
                            }}
                        >

                            {testimonials.map((testimonial) => (

                                <div
                                    key={testimonial.name}
                                    className="shrink-0 px-2 sm:px-2.5"
                                    style={{
                                        width: `${100 / itemsPerView}%`,
                                    }}
                                >

                                    {/* =================================================
                                        TESTIMONIAL CARD
                                    ================================================== */}
                                    <article
                                        className="
                                            group
                                            relative
                                            h-full
                                            rounded-2xl
                                            border
                                            border-[#DFE8ED]
                                            bg-white
                                            p-6
                                            shadow-[0_5px_20px_rgba(6,47,79,0.06)]
                                            transition-all
                                            duration-300
                                            hover:-translate-y-1
                                            hover:border-[#0B6E8E]/30
                                            hover:shadow-[0_15px_35px_rgba(6,47,79,0.12)]
                                        "
                                    >

                                        {/* Quote Icon */}
                                        <div
                                            className="
                                                absolute
                                                right-5
                                                top-5
                                                flex
                                                h-10
                                                w-10
                                                items-center
                                                justify-center
                                                rounded-full
                                                bg-[#EAF6F8]
                                                text-[#0B6E8E]
                                            "
                                        >
                                            <ChatBubbleLeftRightIcon className="h-5 w-5" />
                                        </div>


                                        {/* Stars */}
                                        <div className="flex items-center gap-1">

                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <StarIcon
                                                    key={star}
                                                    className={`h-4 w-4 ${star <= testimonial.rating
                                                        ? "text-[#F7B928]"
                                                        : "text-slate-200"
                                                        }`}
                                                />
                                            ))}

                                        </div>


                                        {/* Review */}
                                        <p className="mt-5 min-h-[130px] pr-3 text-sm leading-7 text-slate-600">
                                            “{testimonial.text}”
                                        </p>


                                        {/* Divider */}
                                        <div className="my-5 h-px bg-[#E8EEF1]" />


                                        {/* Guest */}
                                        <div className="flex items-center gap-3">

                                            <div
                                                className="
                                                    flex
                                                    h-11
                                                    w-11
                                                    shrink-0
                                                    items-center
                                                    justify-center
                                                    rounded-full
                                                    bg-[#062F4F]
                                                    text-sm
                                                    font-extrabold
                                                    text-[#F7B928]
                                                "
                                            >
                                                {testimonial.initials}
                                            </div>

                                            <div className="min-w-0">

                                                <div className="flex items-center gap-1.5">

                                                    <h3 className="text-sm font-bold text-[#062F4F]">
                                                        {testimonial.name}
                                                    </h3>

                                                    <CheckBadgeIcon className="h-4 w-4 text-[#0B6E8E]" />

                                                </div>

                                                <p className="mt-0.5 text-xs text-slate-500">
                                                    {testimonial.location}
                                                </p>

                                            </div>

                                        </div>


                                        {/* Verified */}
                                        <div
                                            className="
                                                mt-5
                                                inline-flex
                                                items-center
                                                gap-1.5
                                                rounded-full
                                                bg-[#F7FAFC]
                                                px-3
                                                py-1.5
                                                text-[10px]
                                                font-bold
                                                uppercase
                                                tracking-wider
                                                text-[#0B6E8E]
                                            "
                                        >
                                            <CheckBadgeIcon className="h-3.5 w-3.5" />

                                            Verified Guest
                                        </div>

                                    </article>

                                </div>

                            ))}

                        </div>

                    </div>


                    {/* Next Button */}
                    <button
                        type="button"
                        onClick={handleNext}
                        aria-label="Next testimonials"
                        className="
                            absolute
                            right-0
                            top-1/2
                            z-20
                            hidden
                            h-11
                            w-11
                            translate-x-1/2
                            -translate-y-1/2
                            items-center
                            justify-center
                            rounded-full
                            border
                            border-[#DFE8ED]
                            bg-white
                            text-[#062F4F]
                            shadow-[0_5px_20px_rgba(6,47,79,0.10)]
                            transition-all
                            duration-300
                            hover:bg-[#062F4F]
                            hover:text-white
                            lg:flex
                        "
                    >
                        <ChevronRightIcon className="h-5 w-5" />
                    </button>

                </div>


                {/* =====================================================
                    MOBILE / TABLET ARROWS
                ====================================================== */}
                <div className="mt-5 flex items-center justify-center gap-3 lg:hidden">

                    <button
                        type="button"
                        onClick={handlePrevious}
                        aria-label="Previous testimonials"
                        className="
                            flex
                            h-10
                            w-10
                            items-center
                            justify-center
                            rounded-full
                            border
                            border-[#DFE8ED]
                            bg-white
                            text-[#062F4F]
                            shadow-sm
                            transition
                            hover:bg-[#062F4F]
                            hover:text-white
                        "
                    >
                        <ChevronLeftIcon className="h-4 w-4" />
                    </button>

                    <button
                        type="button"
                        onClick={handleNext}
                        aria-label="Next testimonials"
                        className="
                            flex
                            h-10
                            w-10
                            items-center
                            justify-center
                            rounded-full
                            border
                            border-[#DFE8ED]
                            bg-white
                            text-[#062F4F]
                            shadow-sm
                            transition
                            hover:bg-[#062F4F]
                            hover:text-white
                        "
                    >
                        <ChevronRightIcon className="h-4 w-4" />
                    </button>

                </div>


                {/* =====================================================
                    DOTS
                ====================================================== */}
                {dotCount > 1 && (
                    <div className="mt-6 flex items-center justify-center gap-2">

                        {Array.from({ length: dotCount }).map((_, index) => (

                            <button
                                key={index}
                                type="button"
                                onClick={() => setCurrentIndex(index)}
                                aria-label={`Go to testimonial slide ${index + 1}`}
                                className={`
                                    h-2
                                    rounded-full
                                    transition-all
                                    duration-300
                                    ${currentIndex === index
                                        ? "w-7 bg-[#0B6E8E]"
                                        : "w-2 bg-[#C9D8DE] hover:bg-[#0B6E8E]/50"
                                    }
                                `}
                            />

                        ))}

                    </div>
                )}


                {/* =====================================================
                    BOTTOM TRUST STRIP
                ====================================================== */}
                <div
                    className="
                        mt-8
                        flex
                        flex-col
                        items-center
                        justify-between
                        gap-4
                        rounded-2xl
                        border
                        border-[#DFE8ED]
                        bg-white
                        px-5
                        py-5
                        text-center
                        shadow-sm
                        sm:flex-row
                        sm:text-left
                        lg:px-7
                    "
                >

                    <div className="flex items-center gap-3">

                        <div
                            className="
                                flex
                                h-10
                                w-10
                                shrink-0
                                items-center
                                justify-center
                                rounded-full
                                bg-[#EAF6F8]
                                text-[#0B6E8E]
                            "
                        >
                            <CheckBadgeIcon className="h-5 w-5" />
                        </div>

                        <div>

                            <p className="text-sm font-bold text-[#062F4F]">
                                Book with confidence
                            </p>

                            <p className="mt-0.5 text-xs text-slate-500">
                                Verified hotels • Secure booking • Local support
                            </p>

                        </div>

                    </div>


                    <div className="flex items-center gap-1">

                        {[1, 2, 3, 4, 5].map((star) => (
                            <StarIcon
                                key={star}
                                className="h-4 w-4 text-[#F7B928]"
                            />
                        ))}

                    </div>

                </div>

            </div>

        </section>
    );
}